import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import LanguageSelector from './LanguageSelector';
import LimitModal from './ExpiredModal';
import ProductTour from './ProductTour';
import RetentionNotice from './RetentionNotice';
import { LogOut, Menu, ChevronLeft, ChevronRight, Shield, Wallet } from 'lucide-react';
import { NAV_ITEMS, NAV_GROUPS, navUnits } from '../constants/nav';
import { canSeeOutreach, visibleNavItems } from '../constants/features';
import { UnreadProvider, useUnread } from '../outreach/UnreadContext';
import './Layout.css';

function formatChars(n) {
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(n);
}


function UnreadBadge() {
  const { t } = useTranslation('common');
  const { unread, overdue, loaded } = useUnread();
  if (!loaded) {
    return <span className="nav-item-unread-loading" aria-label={t('common.loading', 'Loading')} />;
  }
  // Separate badges: a thread that fell through was almost always read already.
  return (
    <>
      {overdue > 0 && (
        <span className="nav-item-overdue" title={t('outreach.overdueCount', { count: overdue })}>
          {overdue > 99 ? '99+' : overdue}
        </span>
      )}
      {unread > 0 && (
        <span className="nav-item-unread" title={t('outreach.unreadCount', { count: unread })}>
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </>
  );
}


function NavItemLink({ item, location, collapsed, onNavigate, nested = false }) {
  const { t } = useTranslation('common');
  const Icon = item.icon;
  const isActive =
    item.path === '/admin'
      ? location.pathname.startsWith('/admin')
      : location.pathname === item.path;
  const className = [
    'nav-item',
    isActive && 'active',
    nested && 'nav-item-nested',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <Link
      to={item.path}
      data-tour={item.tourKey}
      className={className}
      onClick={onNavigate}
      title={collapsed ? item.label : undefined}
    >
      <Icon size={collapsed ? 24 : 18} />
      {!collapsed && <span className="nav-item-label">{item.label}</span>}
      {!collapsed && item.badge === 'free' && (
        <span className="nav-item-badge">{t('badges.free')}</span>
      )}
      {item.badge === 'unread' && <UnreadBadge />}
    </Link>
  );
}


function NavGroup({ group, items, units, location, onNavigate }) {
  const { t } = useTranslation('common');
  const { labelKey, tourKey, variant } = NAV_GROUPS[group];
  // Open state persists per group so a user's choice survives navigation
  // and reload. Auto-opens on mount if the current route belongs to the
  // group so the user always sees where they are.
  const storageKey = `nav-group-open:${tourKey}`;
  const containsActive = items.some((i) => location.pathname === i.path);
  const [open, setOpen] = useState(() => {
    if (containsActive) return true;
    return localStorage.getItem(storageKey) === 'true';
  });
  useEffect(() => {
    if (containsActive && !open) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containsActive]);
  useEffect(() => {
    localStorage.setItem(storageKey, open ? 'true' : 'false');
  }, [open, storageKey]);
  // The product tour spotlights individual items inside this group - it
  // can't reach one that isn't rendered yet, so it asks first.
  useEffect(() => {
    const onFocus = (e) => {
      if (items.some((i) => i.tourKey === e.detail)) setOpen(true);
    };
    window.addEventListener('tour-step-focus', onFocus);
    return () => window.removeEventListener('tour-step-focus', onFocus);
  }, [items]);
  return (
    <div className={`nav-group ${open ? 'open' : ''}`}>
      <button
        type="button"
        className={`nav-group-header${variant === 'primary' ? ' nav-group-header-primary' : ''}`}
        onClick={() => setOpen((v) => !v)}
        data-tour={tourKey}
        aria-expanded={open}
      >
        <ChevronRight size={16} className="nav-group-chevron" />
        <span>{t(labelKey)}</span>
        {/* Folded away, the Inbox item is not rendered - its count must not go with it. */}
        {!open && items.some((item) => item.badge === 'unread') && <UnreadBadge />}
      </button>
      {open && (
        <div className="nav-group-children">
          {units.map((unit) => (unit.kind === 'item' ? (
            <NavItemLink
              key={unit.item.path}
              item={unit.item}
              location={location}
              collapsed={false}
              onNavigate={onNavigate}
              nested
            />
          ) : (
            <NavGroup
              key={unit.group}
              group={unit.group}
              items={unit.items}
              units={unit.units}
              location={location}
              onNavigate={onNavigate}
            />
          )))}
        </div>
      )}
    </div>
  );
}

function LayoutShell() {
  const { t } = useTranslation('common');
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false); // For mobile
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', collapsed.toString());
  }, [collapsed]);

  const navItems = [
    ...visibleNavItems(NAV_ITEMS, user).map((item) => ({ ...item, label: t(item.labelKey) })),
    ...(user?.is_superuser ? [{ path: '/admin', label: t('nav.admin', 'Admin'), icon: Shield }] : []),
  ];

  const renderUnits = navUnits(navItems);

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  return (
    <div className={`layout ${collapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Mobile header */}
      <header className="mobile-header">
        <button className="btn btn-ghost" onClick={() => setSidebarOpen(true)}>
          <Menu size={20} />
        </button>
        {/* The wordmark, not the current feature's name: the sidebar says
            ChatsControl and the mobile bar said Translator on every page. */}
        <span className="mobile-title logo">
          <span className="logo-accent">C</span>
          <span className="logo-rest">hatsControl</span>
        </span>
        <LanguageSelector compact />
      </header>

      {/* Sidebar overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''} ${collapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <h1 className="logo">
            {collapsed ? (
              <span className="logo-accent">C</span>
            ) : (
              <>
                <span className="logo-accent">C</span>
                <span className="logo-rest">hatsControl</span>
              </>
            )}
          </h1>
          <button
            className="btn btn-ghost collapse-btn"
            onClick={toggleCollapse}
            title={collapsed ? t('sidebar.expandSidebar') : t('sidebar.collapseSidebar')}
          >
            {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {renderUnits.map((unit) => {
            if (unit.kind === 'item') {
              return (
                <NavItemLink
                  key={unit.item.path}
                  item={unit.item}
                  location={location}
                  collapsed={collapsed}
                  onNavigate={() => setSidebarOpen(false)}
                />
              );
            }
            // Group. When the sidebar itself is collapsed there's no
            // room for an accordion header - render the children flat
            // as icon-only links so the user can still reach them.
            if (collapsed) {
              return unit.items.map((item) => (
                <NavItemLink
                  key={item.path}
                  item={item}
                  location={location}
                  collapsed={collapsed}
                  onNavigate={() => setSidebarOpen(false)}
                />
              ));
            }
            return (
              <NavGroup
                key={unit.group}
                group={unit.group}
                items={unit.items}
                units={unit.units}
                location={location}
                onNavigate={() => setSidebarOpen(false)}
              />
            );
          })}
        </nav>

        <div className="sidebar-footer">
          {!collapsed && user?.char_usage && !user.char_usage.postpaid && (
            <div className="sidebar-usage-wrap">
              <div className="sidebar-usage">
                <div className="sidebar-usage-bar">
                  <div
                    className="sidebar-usage-fill"
                    style={{ width: `${Math.min(100, (user.char_usage.used_chars / user.char_usage.char_limit) * 100)}%` }}
                  />
                </div>
                <span className="sidebar-usage-text">
                  {formatChars(user.char_usage.used_chars)} / {formatChars(user.char_usage.char_limit)}
                  {user.char_usage.free_topped_up_to > user.char_usage.free_remaining && (
                    <> · {user.char_usage.days_until_reset}d</>
                  )}
                </span>
              </div>
              {user.char_usage.purchased_balance > 0 && (
                <Link to="/usage" className="sidebar-usage-paid" title={t('sidebar.paidTooltip')}>
                  <Wallet size={12} />
                  <span>{t('sidebar.paidLeft', { amount: formatChars(user.char_usage.purchased_balance) })}</span>
                </Link>
              )}
            </div>
          )}
          {!collapsed && <LanguageSelector />}
          {!collapsed && (
            <div className="user-info">
              <div className="user-avatar">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="user-details">
                <span className="user-name">{user?.name}</span>
                <span className="user-email">{user?.email}</span>
              </div>
            </div>
          )}
          <button
            className="btn btn-ghost logout-btn"
            onClick={logout}
            title={collapsed ? t('nav.logout') : undefined}
          >
            <LogOut size={collapsed ? 24 : 18} />
            {!collapsed && <span>{t('nav.logout')}</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content">
        <Outlet />
      </main>

      {/* Global modals */}
      <LimitModal />
      <ProductTour />
      <RetentionNotice />
    </div>
  );
}

function Layout() {
  const { user } = useAuth();
  return (
    <UnreadProvider enabled={canSeeOutreach(user)}>
      <LayoutShell />
    </UnreadProvider>
  );
}

export default Layout;
